import clsx from 'clsx';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Phone-width column, centred on desktop. The whole product is one-handed.
 *
 * `wide` is the desktop exception (review §4.13): home, lot detail and confirm
 * grow to 800px — the middle of the review's 720-880px band — at >=1024px only.
 * Every other page (active session, pay, plates, records…) keeps the 520px
 * column at all widths, and below lg absolutely nothing changes.
 */
export function Screen({
  children,
  className,
  wide,
}: {
  children: ReactNode;
  className?: string;
  wide?: boolean;
}) {
  return (
    <div
      className={clsx(
        'mx-auto flex w-full max-w-[520px] flex-col gap-4 px-[18px] pb-12 pt-5',
        wide && 'lg:max-w-[800px]',
        className,
      )}
    >
      {children}
    </div>
  );
}

export function Title({ children, sub }: { children: ReactNode; sub?: ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <h1 className="text-[28px] font-bold leading-tight tracking-[-0.6px]">{children}</h1>
      {sub ? <p className="text-[13px] leading-relaxed text-muted">{sub}</p> : null}
    </div>
  );
}

export function Muted({ children, className }: { children: ReactNode; className?: string }) {
  return <p className={clsx('text-[13px] leading-relaxed text-muted', className)}>{children}</p>;
}

export function Note({ children }: { children: ReactNode }) {
  return <div className="rounded-lg bg-surface-muted p-3 text-xs leading-relaxed text-muted">{children}</div>;
}

/** Cards and controls share one ~8px radius (review §5); only the active
 *  session card is allowed a stronger container radius. */
export function Card({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={clsx('flex flex-col gap-3 rounded-lg border border-hairline p-4', className)}>
      {children}
    </div>
  );
}

/**
 * The single primary action. Full width, 60px tall, always carrying a second
 * line with the concrete amount or consequence.
 */
export function Cta({
  label,
  note,
  onClick,
  disabled,
  loading,
  tone = 'primary',
  type = 'button',
}: {
  label: string;
  note?: string;
  onClick?: () => void;
  disabled?: boolean;
  loading?: boolean;
  tone?: 'primary' | 'accent' | 'success';
  type?: 'button' | 'submit';
}) {
  const { t } = useTranslation();
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={clsx(
        'flex min-h-[60px] w-full flex-col items-center justify-center gap-0.5 rounded-lg px-4 text-white transition disabled:opacity-40',
        tone === 'primary' && 'bg-ink',
        tone === 'accent' && 'bg-accent',
        tone === 'success' && 'bg-success',
        !disabled && !loading && 'active:brightness-125',
      )}
    >
      {loading ? (
        <span className="text-[15px]">{t('common.loading')}…</span>
      ) : (
        <>
          <span className="text-[17px] font-semibold">{label}</span>
          {note ? <span className="text-xs opacity-75">{note}</span> : null}
        </>
      )}
    </button>
  );
}

export function SecondaryButton({
  label,
  onClick,
  dashed,
}: {
  label: string;
  onClick: () => void;
  dashed?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'w-full rounded-lg border-[1.5px] px-4 py-3 text-[15px] font-semibold text-accent',
        dashed ? 'border-dashed border-hairline-strong' : 'border-hairline',
      )}
    >
      {label}
    </button>
  );
}

export function LinkButton({
  label,
  onClick,
  disabled,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="w-full text-center text-sm text-accent disabled:opacity-40"
    >
      {label}
    </button>
  );
}

export function Row({ label, value, mono }: { label: string; value: ReactNode; mono?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-[15px]">
      <span className="text-muted">{label}</span>
      <span className={clsx('font-semibold', mono && 'num')}>{value}</span>
    </div>
  );
}

export function Badge({
  children,
  tone = 'ink',
}: {
  children: ReactNode;
  tone?: 'ink' | 'success' | 'warning';
}) {
  return (
    <span
      className={clsx(
        'rounded-md px-[7px] py-[3px] text-[10px] font-medium tracking-wide text-white',
        tone === 'ink' && 'bg-ink',
        tone === 'success' && 'bg-success',
        tone === 'warning' && 'bg-warning',
      )}
    >
      {children}
    </span>
  );
}

export function Chip({ children }: { children: ReactNode }) {
  return <span className="rounded-full bg-surface-muted px-3 py-1.5 text-xs">{children}</span>;
}

export function Loading({ label }: { label?: string }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center gap-3 py-16">
      <span className="h-8 w-8 animate-spin rounded-full border-[3px] border-hairline border-t-accent" />
      <Muted>{label ?? t('common.loading')}</Muted>
    </div>
  );
}

/** Offline is a routine state here, so retry is always one tap away. */
export function ErrorState({
  message,
  onRetry,
  hint,
}: {
  message: string;
  onRetry?: () => void;
  hint?: string;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center gap-4 py-14 text-center">
      <p className="text-[15px]">{message}</p>
      {hint ? <Muted>{hint}</Muted> : null}
      {onRetry ? (
        <div className="w-full">
          <Cta label={t('common.retry')} onClick={onRetry} />
        </div>
      ) : null}
    </div>
  );
}
